<template>
    <div class="container mt-5">
        <div class="row">
            <div class="col-12 col-lg-10 col-xl-8 col-3xl-9">
                <h1 class="h5 font-weight-bolder mb-3">
                    <span class="icon icon-glyphicons-basic-power-cord-plug"></span>
                    LMS Integrations
                </h1>
                <p class="text-secondary">
                    Integrate Affinity with your organization's Learning Management Systems using the
                    <a href="http://www.imsglobal.org/spec/lti/v1p3/" target="_blank" rel="noopener">LTI 1.3</a>
                    standard. You can integrate with multiple LMS platforms.
                </p>
                <hr />
                <div>
                    <button class="btn btn-sm btn-primary" @click="openCreateInstance()" :disabled="!organizationLMSFeatureEnabled">Add LMS Integration</button>
                </div>
                <div v-if="lms_instances.length">
                    <br />
                    <ul class="list-group">
                        <li class="list-group-item" v-for="instance in lms_instances" :key="instance.id">
                            <div class="d-flex align-items-center">
                                <div class="flex-grow-1 mr-3">
                                    <p class="mb-1">
                                        <span>{{ instance.name }}</span>
                                    </p>
                                    <p class="text-secondary small mb-1">
                                        Last modified on {{ formatDate(instance.last_modified) }}
                                    </p>
                                    <p class="mb-0 small">
                                        <span class="badge badge-gray mr-1">{{
                                            getVendorDisplayName(instance.lms_type)
                                        }}</span>
                                        <span class="badge badge-gray mr-1" v-if="instance.grade_passback_enabled"
                                            >Results Sync Enabled</span
                                        >
                                        <span class="badge badge-info mr-1" v-if="instance.is_active">Enabled</span>
                                        <span class="badge badge-danger mr-1" v-else>Disabled</span>
                                        <span class="badge badge-warning mr-1" v-if="!instance.is_configured"
                                            >Setup Incomplete</span
                                        >
                                        <span class="badge badge-warning mr-1" v-else-if="!instance.is_verified"
                                            >Unverified</span
                                        >
                                        <span class="badge badge-success mr-1" v-else>Verified</span>
                                    </p>
                                </div>
                                <div class="flex-grow-0">
                                    <button
                                        @click="openRemoveInstance(instance)"
                                        type="button"
                                        class="btn btn-sm btn-gray mr-1"
                                        aria-label="Remove instance"
                                    >
                                        <span class="icon icon-glyphicons-basic-bin"></span>
                                    </button>
                                    <button
                                        @click="openEditInstance(instance)"
                                        type="button"
                                        class="btn btn-sm btn-gray mr-1"
                                        aria-label="Remove instance"
                                    >
                                        <span class="icon icon-glyphicons-basic-cogwheel"></span>
                                    </button>
                                    <button
                                        v-if="instance.is_verified"
                                        @click="openReportModal(instance)"
                                        type="button"
                                        class="btn btn-sm btn-gray mr-1"
                                    >
                                        Reports
                                    </button>
                                    <button
                                        @click="openConfigureInstance(instance)"
                                        type="button"
                                        class="btn btn-sm btn-gray"
                                    >
                                        <span class="icon icon-glyphicons-basic-database"></span>
                                        Setup & Config
                                    </button>
                                    <button
                                        v-if="!instance.is_verified && instance.is_configured"
                                        @click="openVerifyInstance(instance)"
                                        type="button"
                                        class="btn btn-sm btn-warning ml-1"
                                    >
                                        <span class="icon icon-glyphicons-basic-circle-alert mr-1"></span>
                                        <span>Complete Verification</span>
                                    </button>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
                <div v-else-if="organizationLMSFeatureEnabled">
                    <p class="mt-4">You haven't added any LMS integrations yet.</p>
                </div>
                <p v-else class="medium mt-4">
                    To set up an LMS integration, contact us at <a href="mailto:team@affinitylearning.ca?subject=LMS Integration"
                        >team@affinitylearning.ca</a
                    >.
                </p>
            </div>
        </div>
        <!-- Create Modal -->
        <base-modal id="create-instance-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Create New LMS Integration </template>
            <template>
                <base-form v-model="newInstance" :fields="createFormFields" ref="createInstanceForm"></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="createInstance()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    Create
                </button>
            </template>
        </base-modal>
        <!-- Edit Modal -->
        <base-modal id="edit-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> LMS Integration Settings </template>
            <template>
                <base-form v-model="selectedInstance" :fields="internalFields" ref="editInstanceForm"></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="saveInstance()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    Save
                </button>
            </template>
        </base-modal>
        <!-- Remove Modal -->
        <base-modal id="remove-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-100 icon-glyphicons-basic-bin align-middle mr-1"></span>
                <span class="align-middle">Remove Integration</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p>
                            Remove integration with LMS <strong>{{ selectedInstance.name }}</strong
                            >?
                        </p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="removeInstance()" class="btn btn-danger" type="button">Remove & Deactivate</button>
            </template>
        </base-modal>
        <!-- Configuration Modal -->
        <base-modal id="configure-modal" :loading="modalLoading" :centered="true" size="large">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-cogwheel align-middle mr-1"></span>
                <span class="align-middle">Integration Setup</span>
            </template>
            <template>
                <div>
                    <div class="mb-4">
                        <span class="badge badge-gray">LMS Type: {{ selectedVendorTypeName }}</span>
                    </div>
                    <div>
                        <span class="mb-2 badge badge-secondary">Step 1</span>
                        <p class="text-secondary small mb-1">
                            Create an external tool in your LMS with the following data fields.
                        </p>
                        <hr class="mt-2 mb-3" />
                    </div>
                    <ul class="no-style mb-4">
                        <li v-for="field in toolToLmsFields" :key="field">
                            <p class="mb-0 medium">{{ selectedVendorFieldNameMap.toLMS[field] }}:</p>
                            <copy-link-input
                                ref="copyInput"
                                v-model="selectedInstance[field]"
                                success-message="Copied to clipboard"
                                copy-btn-label="Copy"
                                class="mb-3"
                            />
                        </li>
                    </ul>
                    <div>
                        <!-- Vendor-Specific Settings -->
                        <span class="mb-2 badge badge-gray">Additional Required Settings</span>
                        <div v-if="selectedInstance.lms_type == 'd2l'">
                            <ol class="medium">
                                <li>
                                    Under <strong>Extensions</strong>, check the
                                    <strong>Assignments and Grade Services</strong> checkbox.
                                </li>
                                <li>
                                    Under <strong>Extensions</strong>, check the
                                    <strong>Deep Linking</strong> checkbox.
                                </li>
                                <li>
                                    Ensure that <strong>Open as an External Resource</strong> and
                                    <strong>Auto Create Grade Items</strong> are both checked.
                                </li>
                                <li>
                                    Click on View Deployments -> New Deployment to share Affinity with desired org
                                    units.
                                    <br>
                                    Under
                                    <strong>Security Settings</strong>, ensure that the following are
                                    <strong>checked</strong>:
                                    <ul>
                                        <li>Org Unit Information</li>
                                        <li>Email</li>
                                        <li>User ID</li>
                                        <li>Link Information (Title & Description)</li>
                                        <li>Open as an External Resource</li>
                                    </ul>
                                    Under <strong>Configuration Settings</strong>, ensure that the following are
                                    <strong>checked</strong>:
                                    <ul>
                                        <li>Open as External Resource</li>
                                        <li>Auto Create Grade Items</li>
                                    </ul>
                                </li>
                                <li>
                                    Scroll down to the bottom of <strong>Deployment</strong> view, and click
                                    on <strong>View Links</strong>:
                                    <ul>
                                        <li>Click on <strong>New Link</strong>.</li>
                                        <li>
                                            Under <strong>Name</strong>, enter:
                                            <copy-link-input
                                                ref="copyInput"
                                                v-model="selectedInstance.tool_name"
                                                success-message="Copied to clipboard"
                                                copy-btn-label="Copy"
                                                class="py-2 copy-input-sm"
                                            />
                                        </li>
                                        <li>
                                            Under <strong>URL</strong>, enter:
                                            <copy-link-input
                                                ref="copyInput"
                                                v-model="selectedInstance.default_launch_url"
                                                success-message="Copied to clipboard"
                                                copy-btn-label="Copy"
                                                class="py-2 copy-input-sm"
                                            />
                                        </li>
                                        <li>
                                            Under <strong>Type</strong>, select <strong>Deep Linking Quicklink</strong>.
                                        </li>
                                        <li>
                                            Beside <strong>Width</strong> and <strong>Height</strong>, enter <strong>900</strong>.
                                        </li>
                                        <li>
                                            Press <strong>Save and Close</strong>.
                                        </li>
                                    </ul>
                                </li>
                            </ol>
                        </div>
                        <div v-else-if="selectedInstance.lms_type == 'moodle'">
                            <ol class="medium">
                                <li>
                                    Beside <strong>Tool configuration usage</strong>, select
                                    <strong>Show in activity chooser and as a preconfigured tool</strong>.
                                </li>
                                <li>
                                    Beside <strong>Default Launch Container</strong>, select
                                    <strong>New window</strong>.
                                </li>
                                <li>
                                    Check the <strong>Supports Deep Linking</strong> checkbox.
                                </li>
                                <li>
                                    Press <strong>Show More</strong>, and beside <strong>Secure icon URL</strong>, enter:
                                    <copy-link-input
                                        ref="copyInput"
                                        v-model="faviconUrl"
                                        success-message="Copied to clipboard"
                                        copy-btn-label="Copy"
                                        class="py-2 copy-input-sm"
                                    />
                                </li>
                                <li>
                                    Under <strong>Content Selection URL</strong>, enter:
                                    <copy-link-input
                                        ref="copyInput"
                                        v-model="selectedInstance.default_launch_url"
                                        success-message="Copied to clipboard"
                                        copy-btn-label="Copy"
                                        class="py-2 copy-input-sm"
                                    />
                                </li>
                                <li>
                                    Under <strong>Services</strong>, enter the following settings:
                                    <ul>
                                        <li>
                                            IMS LTI Assignment and Grade Services:
                                            <strong>Use this service for Results Sync and column management</strong>
                                        </li>
                                        <li>
                                            Tool Settings:
                                            <strong>Use this service</strong>
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    Under <strong>Privacy</strong>, enter the following settings:
                                    <ul>
                                        <li>
                                            Share launcher's email with tool:
                                            <strong>Always</strong>
                                        </li>
                                        <li>
                                            Accept grades from this tool:
                                            <strong>Always</strong>
                                        </li>
                                    </ul>
                                </li>
                            </ol>
                        </div>
                        <div v-else-if="selectedInstance.lms_type == 'canvas'">
                            <ol class="medium">
                                <li>
                                    Under <strong>Configure</strong> -> <strong>Method</strong>, select <strong>Enter URL</strong>.
                                </li>
                            </ol>
                        </div>
                    </div>
                    <div class="mt-4 pt-2">
                        <span class="mb-2 badge badge-secondary">Step 2</span>
                        <p class="text-secondary small mb-1">
                            Copy the data fields provided by your LMS into Affinity.
                        </p>
                        <hr class="mt-2" />
                    </div>
                    <div>
                        <base-form
                            v-model="selectedInstance"
                            :fields="lmsToToolFields"
                            ref="configureInstanceForm"
                        ></base-form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="saveConfigDetails()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    Save & Verify
                </button>
            </template>
        </base-modal>
        <!-- Verification Modal -->
        <base-modal id="verify-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="align-middle">Verify Your Integration</span>
            </template>
            <template>
                <div v-if="verificationComplete" class="py-5">
                    <p class="h5 text-center mb-2">
                        <span class="icon icon-glyphicons-basic-circle-check text-success align-middle mr-1"></span>
                        <span class="align-middle">Verification Complete</span>
                    </p>
                    <p class="text-secondary text-center mb-0">
                        The integration with this LMS is now verified.
                    </p>
                </div>
                <div v-else>
                    <div>
                        <span class="mb-2 badge badge-secondary">Step 3</span>
                        <p class="text-secondary small mb-1">Initiate a test request from your LMS.</p>
                        <hr class="mt-2" />
                    </div>
                    <div class="mb-4 pb-3">
                        <p class="mb-2">Initiate a verification request from your LMS using the LTI link below:</p>
                        <copy-link-input
                            v-if="selectedInstance.default_launch_url"
                            v-model="selectedInstance.default_launch_url"
                            class="mb-3"
                        />
                        <lms-instructions
                            :default-vendor-type="selectedInstance.lms_type"
                            :show-launch-step="true"
                            :show-by-default="true"
                        ></lms-instructions>
                    </div>
                    <div class="mb-2">
                        <button
                            class="btn btn-sm btn-primary"
                            @click="loadLaunchRequest()"
                            :disabled="launchRequestsLoading"
                        >
                            <span v-show="!launchRequest.id">
                                I have completed the steps above
                            </span>
                            <span v-show="launchRequest.id">
                                Refresh requests
                            </span>
                            <div
                                class="spinner-border spinner-border-tiny text-white ml-2"
                                v-show="launchRequestsLoading"
                                role="status"
                            >
                                <span class="sr-only">Loading...</span>
                            </div>
                        </button>
                    </div>
                    <div v-if="launchRequest.id">
                        <p class="text-secondary medium">
                            <span class="icon icon-glyphicons-basic-circle-empty-check text-success mr-1 align-middle"></span>
                            <span class="align-middle">LTI request received:</span>
                            <ul>
                                <li>
                                    <strong>Request received on:</strong> {{ formatDate(launchRequest.created) }}
                                </li>
                                <li>
                                    <strong>LMS Issuer:</strong> {{ launchRequest.lms_issuer_received }}
                                </li>
                                <li>
                                    <strong>LMS ID:</strong> {{ launchRequest.lms_instance_id_received }}
                                </li>
                            </ul>
                        </p>
                        <button class="btn btn-sm btn-success" @click="verifyLaunchRequest()" :disabled="verificationIsLoading">
                            <span class="icon icon-100 icon-glyphicons-basic-check mr-1"></span>
                            This was me
                        </button>
                    </div>
                    <div v-else>
                        <p class="text-secondary medium mt-3">
                            No requests have been received from this LMS yet.
                        </p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            </template>
        </base-modal>
        <!-- Report Modal -->
        <base-modal id="report-modal" :loading="modalLoading" :centered="true">
            <template slot="title">Learner Report - {{ selectedInstance.name }}</template>
            <template>
                <p class="text-secondary mb-2">
                    Download a list of learners and the course IDs they launched into Affinity from, within the provided
                    timeframe.
                </p>
                <base-form v-model="lmsReportConfig" :fields="lmsReportFields"></base-form>
            </template>
            <template slot="footer">
                <form method="GET" :action="lmsReportDownloadUrl">
                    <input hidden style="display: none" name="startDate" :value="lmsReportConfig.startDate" />
                    <input hidden style="display: none" name="endDate" :value="lmsReportConfig.endDate" />
                    <button :disabled="!lmsReportDownloadEnabled" class="btn btn-gray" type="submit">
                        <span class="icon mr-2 icon-glyphicons-basic-cloud-download"></span>Export Data
                    </button>
                </form>
            </template>
        </base-modal>
        <billing-view ref="billingView"></billing-view>
    </div>
</template>

<script>
import Utilities from "@/assets/js/utilities.js"
import http from "@/axios.js"
import BaseForm from "@/components/BaseForm"
import BaseModal from "@/components/BaseModal"
import CopyLinkInput from "@/components/CopyLinkInput"
import LmsInstructions from "@/components/LmsInstructions"
import BillingView from "@/components/BillingView"

export default {
    name: "LMSIntegrationSettings",
    components: {
        BaseForm,
        BaseModal,
        CopyLinkInput,
        LmsInstructions,
        BillingView,
    },
    data() {
        return {
            modalLoading: false,
            launchRequestsLoading: false,
            launchRequest: {},
            verificationIsLoading: false,
            verificationComplete: false,
            lms_instances: [],
            newInstance: {},
            selectedInstance: {},
            createFormFields,
            internalFields,
            lmsReportFields,
            lmsReportConfig: {},
            faviconUrl: "https://app.affinitylearning.ca/static/general/img/affinity-favicon.png",
        }
    },
    computed: {
        currentOrganizationID() {
            return this.$store.getters.currentOrganizationId
        },
        organizationLMSFeatureEnabled() {
            return this.$store.getters.organizationDetails.ff_lms_integration
        },
        organizationBillingEnabled() {
            return this.$store.getters.organizationBillingEnabled
        },
        lmsReportDownloadEnabled() {
            return this.lmsReportConfig.startDate && this.lmsReportConfig.endDate
        },
        lmsReportDownloadUrl() {
            return `/api/lms/${this.selectedInstance.id}/learner-report/`
        },
        // Returns the display-friendly name for a given LMS vendor.
        selectedVendorTypeName() {
            if (!this.selectedInstance.lms_type) {
                return ""
            }
            return vendorNames[this.selectedInstance.lms_type]
        },
        // Returns the field name map based on the currently selected LMS type.
        selectedVendorFieldNameMap() {
            if (!this.selectedInstance.lms_type) {
                return null
            }
            return vendorFieldNameMap[this.selectedInstance.lms_type]
        },
        // A list of field keys for fields that must be copied from this application
        // into the LMS platform.
        toolToLmsFields() {
            if (!this.selectedVendorFieldNameMap) {
                return []
            }
            return Object.keys(this.selectedVendorFieldNameMap.toLMS)
        },
        // A list of fields (as config dictionaries) required to be copied from the
        // LMS platform.
        lmsToToolFields() {
            if (!this.selectedVendorFieldNameMap) {
                return []
            }
            let vm = this
            let fieldKeys = Object.keys(this.selectedVendorFieldNameMap.fromLMS)
            return fieldKeys.map((key) => {
                return {
                    ...lmsConfigFields[key],
                    key: key,
                    name: vm.selectedVendorFieldNameMap.fromLMS[key],
                    required: true,
                }
            })
        },
    },
    methods: {
        formatDate(date) {
            return Utilities.formatDateTime(date)
        },
        getVendorDisplayName(lmsType) {
            return vendorNames[lmsType]
        },
        async fetchData() {
            this.$store.dispatch("addLoad")
            // Fetch the user details if they're not populated in the store yet.
            if (!this.currentOrganizationID) {
                await this.$store.dispatch("fetchAuth")
            }
            let vm = this
            http.get(`/api/organizations/${this.currentOrganizationID}/lms/`)
                .then(function (response) {
                    vm.lms_instances = response.data
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
            // Refesh organization details to make sure they're up to date. If LMS instances
            // have been set up or deactivated, this will ensure that certain features are
            // enabled/disabled appropriately elsehwere in the application.
            vm.$store.dispatch("fetchOrganizationDetails")
        },
        openCreateInstance() {
            this.newInstance = {
                is_active: true,
                lms_type: "canvas",
                grade_passback_enabled: true,
                show_community_items_in_dp_link: true,
            }
            this.$refs.createInstanceForm.resetValidate()
            $("#create-instance-modal").modal("show")
        },
        openEditInstance(selectedInstance) {
            this.selectedInstance = Object.assign({}, selectedInstance)
            this.$refs.editInstanceForm.resetValidate()
            $("#edit-modal").modal("show")
        },
        openRemoveInstance(selectedInstance) {
            this.selectedInstance = Object.assign({}, selectedInstance)
            $("#remove-modal").modal("show")
        },
        openConfigureInstance(selectedInstance) {
            this.selectedInstance = Object.assign({}, selectedInstance)
            let vm = this
            this.$nextTick(() => vm.$refs.copyInput.forEach((input) => input.reset()))
            $("#configure-modal").modal("show")
        },
        openVerifyInstance(selectedInstance) {
            this.selectedInstance = Object.assign({}, selectedInstance)
            this.launchRequest = {}
            this.verificationComplete = false
            $("#verify-modal").modal("show")
        },
        openReportModal(selectedInstance) {
            this.selectedInstance = Object.assign({}, selectedInstance)
            $("#report-modal").modal("show")
        },
        createInstance() {
            if (!this.$refs.createInstanceForm.validate()) {
                return
            }
            let vm = this
            this.modalLoading = true
            http.post(`/api/organizations/${this.currentOrganizationID}/lms/`, this.newInstance)
                .then(function (response) {
                    $("#create-instance-modal").modal("hide")
                    vm.fetchData()
                    vm.selectedInstance = response.data
                    // Show the configuration modal.
                    $("#configure-modal").modal("show")
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        saveInstance() {
            if (!this.$refs.editInstanceForm.validate()) {
                return
            }
            let vm = this
            this.modalLoading = true
            http.put(`/api/lms/${this.selectedInstance.id}/basic/`, this.selectedInstance)
                .then(function () {
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "LMS platform updated.",
                        data: {},
                    })
                    vm.fetchData()
                    $("#edit-modal").modal("hide")
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        saveConfigDetails() {
            if (!this.$refs.configureInstanceForm.validate()) {
                return
            }
            let vm = this
            this.modalLoading = true
            http.put(`/api/lms/${this.selectedInstance.id}/`, this.selectedInstance)
                .then(function () {
                    $("#configure-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "LMS platform updated.",
                        data: {},
                    })
                    vm.fetchData()
                    // Show verify modal to complete verification of the integration with
                    // the instance.
                    $("#verify-modal").modal("show")
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        removeInstance() {
            let vm = this
            this.modalLoading = true
            http.put(`/api/lms/${this.selectedInstance.id}/remove/`)
                .then(function () {
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "LMS platform removed.",
                        data: {},
                    })
                    vm.fetchData()
                    $("#remove-modal").modal("hide")
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Load the most recent launch request for the selected instance.
        loadLaunchRequest() {
            let vm = this
            this.launchRequestsLoading = true
            http.get(`/api/lms/${this.selectedInstance.id}/requests/last/`)
                .then(function (response) {
                    vm.launchRequest = response.data
                })
                .finally(function () {
                    vm.launchRequestsLoading = false
                })
        },
        // Mark a launch request as verified to verify the integration with the selected instance.
        verifyLaunchRequest() {
            let vm = this
            this.verificationIsLoading = true
            http.put(`/api/lms/${this.selectedInstance.id}/requests/${this.launchRequest.id}/verify/`)
                .then(function () {
                    vm.verificationComplete = true
                    vm.fetchData()
                })
                .finally(function () {
                    vm.verificationIsLoading = false
                })
        },
    },
    mounted() {
        this.fetchData()
    },
}

// Display-friendly names for each LMS type.
const vendorNames = {
    d2l: "D2L",
    canvas: "Canvas",
    moodle: "Moodle",
}

// A dictionary of field names used to build forms that are customized to each LMS type.
const lmsConfigFields = {
    install_url: {
        description:
            "The URL where your LMS instance is hosted, including the https:// prefix. Remove trailing slashes.",
        placeholder: "https://myschool.lmsplatform.com",
    },
    lms_access_token_url: {
        description: "The LMS URL to request LTI access tokens from.",
        placeholder: "https://myschool.lmsplatform.com/lti/token",
    },
    lms_oidc_auth_endpoint: {
        description: "The LMS URL to send authentication requests to during an LTI launch.",
        placeholder: "https://myschool.lmsplatform.com/lti/security/jwks",
    },
    lms_jwk_url: {
        description: "A URL where the LMS's public JWKs can be found.",
        placeholder: "https://myschool.lmsplatform.com/lti/security/jwks",
    },
    lms_client_id: {
        description: "The client ID for Affinity in the LMS.",
    },
    lms_issuer_identifier: {
        description:
            "The issuer value is a case-sensitive URL using the HTTPS scheme that contains scheme, host and optionally, port number, and path components.",
    },
}

// Used to convert LTI configuration field names used by this application
// into names used by each LMS vendor in their integration setup UIs.
const vendorFieldNameMap = {
    d2l: {
        toLMS: {
            tool_name: "Name",
            domain_with_prefix: "Domain",
            oidc_redirect_endpoint: "Redirect URLs",
            oidc_auth_endpoint: "OpenID Connect Login URL",
            default_launch_url: "Target Link URI",
            jwk_url: "Keyset URL",
        },
        fromLMS: {
            lms_client_id: "Client Id",
            lms_jwk_url: "Brightspace Keyset URL",
            lms_access_token_url: "Brightspace OAuth2 Access Token URL",
            lms_oidc_auth_endpoint: "OpenID Connect Authentication Endpoint",
            lms_issuer_identifier: "Issuer",
        },
    },
    moodle: {
        toLMS: {
            tool_name: "Name",
            default_launch_url: "Tool URL",
            lti_version: "LTI Version",
            jwk_url: "Public Keyset",
            oidc_auth_endpoint: "Initiate Login URL",
            oidc_redirect_endpoint: "Redirection URI(s)",
        },
        fromLMS: {
            lms_issuer_identifier: "Platform ID",
            lms_client_id: "Client ID",
            lms_jwk_url: "Public Keyset URL",
            lms_access_token_url: "Access Token URL",
            lms_oidc_auth_endpoint: "Authentication Request URL",
        },
    },
    canvas: {
        toLMS: {
            tool_name: "Key Name",
            json_config_url: "JSON URL",
            oidc_redirect_endpoint: "Redirection URI(s)",
        },
        fromLMS: {
            install_url: "Install URL",
            lms_client_id: "Developer Key",
        },
    },
}

// Form fields for creating a new LMS integration.
const createFormFields = [
    {
        name: "LMS Type",
        key: "lms_type",
        description: "The type of LMS you're integrating with.",
        required: true,
        type: "radio",
        options: [
            {
                label: "Canvas",
                value: "canvas",
            },
            {
                label: "D2L Brightspace",
                value: "d2l",
            },
            {
                label: "Moodle",
                value: "moodle",
            },
        ],
    },
    {
        name: "Name",
        key: "name",
        subtext: "A name to identify this LMS (e.g. MySchool Brightspace).",
        required: true,
    },
    {
        name: "Enable Results Sync",
        key: "grade_passback_enabled",
        description: "Enable grades to be posted back to your LMS when learners finish scenarios.",
        type: "switch",
        required: false,
    },
]

// Form fields for internal LMS instance settings.
const internalFields = [
    {
        name: "Internal Name",
        key: "name",
        subtext: "A name to identify this LMS integration.",
        required: true,
    },
    {
        name: "Integration Enabled",
        key: "is_active",
        description: "Turn this switch off to disable all usage of this integration.",
        type: "switch",
        required: false,
    },
    {
        name: "Enable Results Sync",
        key: "grade_passback_enabled",
        description: "Enable grades to be posted back to your LMS when learners finish scenarios.",
        type: "switch",
        required: false,
    },
    {
        name: "Show Community Library items during content selection",
        key: "show_community_items_in_dp_link",
        description:
            "When enabled, instructors in your LMS will be able to add learning experiences from the Community Library to their courses.",
        type: "switch",
        required: false,
    },
]

// Form fields used when running LMS instance reports.
const lmsReportFields = [
    {
        name: "Start Date",
        key: "startDate",
        type: "date",
        required: true,
    },
    {
        name: "End Date",
        key: "endDate",
        type: "date",
        required: true,
    },
]
</script>

<style lang="scss" scoped>
ul.no-style {
    padding-inline-start: 0px;
    li {
        list-style-type: none;
    }
}

.copy-input-sm {
    max-width: 400px;
}
</style>